import { makeObservable, observable, action, runInAction } from 'mobx'
import { signalRService, type MissedCallEvent } from '../services/signalRService'

export interface MissedCallAlert {
  id: string
  event: MissedCallEvent
  remainingSeconds: number
  isPaused: boolean
  createdAt: number
}

export class MissedCallStore {
  alerts: MissedCallAlert[] = []
  private timerId: ReturnType<typeof setInterval> | null = null
  private unsubscribeMissedCall: (() => void) | null = null

  constructor() {
    makeObservable(this, {
      alerts: observable,
      addAlert: action,
      dismissAlert: action,
      setPaused: action,
      tick: action
    })

    this.unsubscribeMissedCall = signalRService.onMissedCallReceived(
      action((event: MissedCallEvent) => this.addAlert(event))
    )

    this.startTimer()
  }

  addAlert(event: MissedCallEvent) {
    const alertId = `${event.callId}_${Date.now()}`
    const newAlert: MissedCallAlert = {
      id: alertId,
      event,
      remainingSeconds: 25,
      isPaused: false,
      createdAt: Date.now()
    }

    // Keep max 3 most recent alerts
    this.alerts = [newAlert, ...this.alerts.slice(0, 2)]
  }

  dismissAlert(id: string) {
    this.alerts = this.alerts.filter(a => a.id !== id)
  }

  setPaused(id: string, isPaused: boolean) {
    const alert = this.alerts.find(a => a.id === id)
    if (alert) {
      alert.isPaused = isPaused
    }
  }

  private startTimer() {
    if (this.timerId) return
    this.timerId = setInterval(() => {
      runInAction(() => {
        this.tick()
      })
    }, 1000)
  }

  tick() {
    this.alerts = this.alerts
      .map(alert => {
        if (alert.isPaused) return alert
        return {
          ...alert,
          remainingSeconds: alert.remainingSeconds - 1
        }
      })
      .filter(alert => alert.remainingSeconds > 0)
  }

  dispose() {
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
    if (this.unsubscribeMissedCall) {
      this.unsubscribeMissedCall()
      this.unsubscribeMissedCall = null
    }
  }
}

export const missedCallStore = new MissedCallStore()
